import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { EnvelopeIcon, ArrowRightIcon, ArrowLeftIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { authApi } from '../services/api';

const schema = z.object({
  email: z.string().email('E-mail inválido'),
});
type FormData = z.infer<typeof schema>;

const PARTICLES = Array.from({ length: 18 }, (_, i) => ({
  id: i,
  left: `${((i * 17 + 7) % 97) + 1}%`,
  bottom: `${(i * 13) % 30}px`,
  delay: `${(i * 0.6) % 9}s`,
  duration: `${7 + (i % 5)}s`,
  size: i % 4 === 0 ? 3 : i % 3 === 0 ? 2 : 1.5,
  drift: `${(i % 2 === 0 ? 1 : -1) * (8 + (i % 18))}px`,
}));

export default function RequestRegistrationPage() {
  const [sent, setSent] = useState(false);
  const [sentEmail, setSentEmail] = useState('');
  const navigate = useNavigate();

  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  const onSubmit = async (data: FormData) => {
    await authApi.requestRegistration(data.email);
    setSentEmail(data.email);
    setSent(true);
  };

  return (
    <div
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      style={{ background: '#0a0f1e' }}
    >
      {/* Grid */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `
            linear-gradient(rgba(0,212,255,0.025) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0,212,255,0.025) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px',
          animation: 'gridScroll 20s linear infinite',
        }}
      />

      {/* Orbs */}
      <div className="absolute pointer-events-none" style={{ top: '-15%', right: '-10%', width: '55vw', height: '55vw', borderRadius: '50%', background: 'radial-gradient(circle, rgba(0,212,255,0.07) 0%, transparent 70%)', animation: 'orb1 12s ease-in-out infinite' }} />
      <div className="absolute pointer-events-none" style={{ bottom: '-10%', left: '-8%', width: '45vw', height: '45vw', borderRadius: '50%', background: 'radial-gradient(circle, rgba(0,100,180,0.08) 0%, transparent 70%)', animation: 'orb2 15s ease-in-out infinite' }} />

      {/* Partículas */}
      {PARTICLES.map((p) => (
        <div key={p.id} className="absolute pointer-events-none rounded-full" style={{ left: p.left, bottom: p.bottom, width: p.size, height: p.size, background: '#00d4ff', opacity: 0, animation: `particleUp ${p.duration} ${p.delay} infinite linear`, '--drift': p.drift } as React.CSSProperties} />
      ))}

      {/* Card */}
      <div className="relative z-10 w-full max-w-sm px-4 py-8" style={{ animation: 'fadeSlideUp 0.6s ease-out both' }}>

        {/* Logo */}
        <div style={{ animation: 'logoFloat 6s ease-in-out infinite', marginBottom: '2px' }}>
          <img src="/img/inoveitech_logo.svg" alt="IncluirTech" className="w-full" style={{ borderRadius: '12px 12px 0 0', display: 'block' }} />
        </div>

        <div
          style={{
            background: 'rgba(255,255,255,0.03)',
            backdropFilter: 'blur(24px)',
            WebkitBackdropFilter: 'blur(24px)',
            border: '1px solid rgba(0,212,255,0.15)',
            borderTop: 'none',
            borderRadius: '0 0 16px 16px',
            padding: '32px',
            boxShadow: '0 0 0 1px rgba(0,212,255,0.1), 0 20px 60px rgba(0,0,0,0.6), 0 0 80px rgba(0,212,255,0.06)',
          }}
        >
          {!sent ? (
            <>
              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
                <div style={{ flex: 1, height: 1, background: 'rgba(0,212,255,0.15)' }} />
                <span style={{ color: 'rgba(0,212,255,0.5)', fontSize: 10, fontWeight: 600, letterSpacing: 3, textTransform: 'uppercase' }}>
                  Criar Conta
                </span>
                <div style={{ flex: 1, height: 1, background: 'rgba(0,212,255,0.15)' }} />
              </div>

              <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', marginBottom: 24, lineHeight: 1.6, textAlign: 'center' }}>
                Informe seu e-mail e enviaremos um link de confirmação para continuar o cadastro.
              </p>

              <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 500, color: 'rgba(255,255,255,0.5)', marginBottom: 8, letterSpacing: 1, textTransform: 'uppercase' }}>
                    E-mail
                  </label>
                  <div style={{ position: 'relative' }}>
                    <EnvelopeIcon style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', width: 16, height: 16, color: 'rgba(0,212,255,0.4)' }} />
                    <input
                      type="email"
                      autoComplete="email"
                      placeholder="seu@email.com"
                      {...register('email')}
                      style={{
                        width: '100%', paddingLeft: 42, paddingRight: 16, paddingTop: 12, paddingBottom: 12,
                        background: 'rgba(0,0,0,0.3)', border: errors.email ? '1px solid rgba(239,68,68,0.5)' : '1px solid rgba(0,212,255,0.15)',
                        borderRadius: 8, color: '#fff', fontSize: 14, outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.2s',
                      }}
                      onFocus={(e) => { e.target.style.borderColor = 'rgba(0,212,255,0.5)'; }}
                      onBlur={(e)  => { e.target.style.borderColor = errors.email ? 'rgba(239,68,68,0.5)' : 'rgba(0,212,255,0.15)'; }}
                    />
                  </div>
                  {errors.email && <p style={{ marginTop: 4, fontSize: 11, color: 'rgb(239,68,68)' }}>{errors.email.message}</p>}
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  style={{
                    marginTop: 8, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                    padding: '14px 24px', background: isSubmitting ? 'rgba(0,212,255,0.3)' : 'linear-gradient(135deg, #00b4d8 0%, #00d4ff 50%, #00e5ff 100%)',
                    border: 'none', borderRadius: 8, color: '#0a0f1e', fontWeight: 700, fontSize: 14, letterSpacing: 1,
                    cursor: isSubmitting ? 'not-allowed' : 'pointer', transition: 'all 0.2s', boxShadow: isSubmitting ? 'none' : '0 4px 24px rgba(0,212,255,0.35)',
                  }}
                >
                  {isSubmitting ? (
                    <span style={{ display: 'inline-block', width: 16, height: 16, border: '2px solid #0a0f1e', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
                  ) : (
                    <>ENVIAR LINK <ArrowRightIcon style={{ width: 16, height: 16 }} /></>
                  )}
                </button>
              </form>

              <div style={{ marginTop: 20, textAlign: 'center' }}>
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: 'rgba(0,212,255,0.6)', fontSize: 12, cursor: 'pointer', padding: 0 }}
                >
                  <ArrowLeftIcon style={{ width: 13, height: 13 }} />
                  Voltar para o login
                </button>
              </div>
            </>
          ) : (
            /* Estado de sucesso */
            <div style={{ textAlign: 'center', animation: 'fadeSlideUp 0.5s ease-out both' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 28 }}>
                <div style={{ flex: 1, height: 1, background: 'rgba(0,212,255,0.15)' }} />
                <span style={{ color: 'rgba(0,212,255,0.5)', fontSize: 10, fontWeight: 600, letterSpacing: 3, textTransform: 'uppercase' }}>E-mail Enviado</span>
                <div style={{ flex: 1, height: 1, background: 'rgba(0,212,255,0.15)' }} />
              </div>

              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
                <CheckCircleIcon style={{ width: 56, height: 56, color: '#00d4ff' }} />
              </div>

              <h3 style={{ fontSize: 18, fontWeight: 700, color: '#f1f5f9', marginBottom: 12 }}>Verifique seu e-mail</h3>
              <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', lineHeight: 1.7, marginBottom: 8 }}>
                Enviamos um link de confirmação para:
              </p>
              <p style={{ fontSize: 14, fontWeight: 600, color: 'rgba(0,212,255,0.9)', marginBottom: 24, wordBreak: 'break-all' }}>
                {sentEmail}
              </p>
              <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)', lineHeight: 1.6, marginBottom: 28 }}>
                O link expira em <strong style={{ color: 'rgba(255,255,255,0.5)' }}>24 horas</strong>. Verifique também a pasta de spam.
              </p>

              <button
                type="button"
                onClick={() => navigate('/login')}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: 'rgba(0,212,255,0.6)', fontSize: 12, cursor: 'pointer', padding: 0 }}
              >
                <ArrowLeftIcon style={{ width: 13, height: 13 }} />
                Voltar para o login
              </button>
            </div>
          )}
        </div>

        <p style={{ textAlign: 'center', marginTop: 20, fontSize: 10, color: 'rgba(255,255,255,0.15)', letterSpacing: 1 }}>
          © {new Date().getFullYear()} INCLUIRTECH · TODOS OS DIREITOS RESERVADOS
        </p>
      </div>

      <style>{`
        @keyframes gridScroll { from { background-position: 0 0; } to { background-position: 50px 50px; } }
        @keyframes orb1 { 0%,100% { transform: translate(0,0) scale(1); } 33% { transform: translate(-3%,4%) scale(1.05); } 66% { transform: translate(2%,-2%) scale(0.97); } }
        @keyframes orb2 { 0%,100% { transform: translate(0,0) scale(1); } 40% { transform: translate(4%,-5%) scale(1.08); } 70% { transform: translate(-2%,3%) scale(0.95); } }
        @keyframes particleUp { 0% { transform: translateY(0) translateX(0); opacity: 0; } 8% { opacity: 0.8; } 88% { opacity: 0.5; } 100% { transform: translateY(-130px) translateX(var(--drift)); opacity: 0; } }
        @keyframes logoFloat { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-5px); } }
        @keyframes fadeSlideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes spin { to { transform: rotate(360deg); } }
        input::placeholder { color: rgba(255,255,255,0.2); }
        input:-webkit-autofill { -webkit-box-shadow: 0 0 0 100px rgba(0,0,0,0.6) inset !important; -webkit-text-fill-color: #fff !important; caret-color: #fff; }
      `}</style>
    </div>
  );
}
